/**
 * Formatos que se repiten en todas las pantallas.
 *
 * Las horas se pintan SIEMPRE en la zona de la clínica, no en la del
 * navegador. Un mensaje de las 20:08 en Condesa, visto desde Madrid con la
 * zona local, saldría como las 4:08 del día siguiente: otra hora y otro día.
 * Y el servidor corta los días en esa misma zona, así que dejar que el
 * navegador decida haría que la tabla y la gráfica de una misma pantalla no
 * cuadraran.
 *
 * La zona la manda el backend en `GET /api/session` — ver `setDisplayTimezone`.
 */
import dayjs from 'dayjs'
import 'dayjs/locale/es'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.locale('es')

/** Respaldo por si se formatea algo antes de que responda la sonda de sesión. */
export const DEFAULT_TIMEZONE = 'America/Mexico_City'

let displayTimezone = DEFAULT_TIMEZONE

/** Lo llama el store de sesión con la zona que declaró el servidor. */
export function setDisplayTimezone(zone: string) {
  if (zone) displayTimezone = zone
}

export function getDisplayTimezone(): string {
  return displayTimezone
}

/**
 * Una fecha de calendario en la zona de la CLÍNICA, en el formato que espera
 * la API (`2026-09-09`). `clinicDate(-1)` es ayer.
 *
 * Calcularla con la zona del navegador se equivocaría de día para quien mire
 * desde otro huso: a las 00:30 en Madrid, "ayer" en México todavía es hoy.
 */
export function clinicDate(offsetDays = 0): string {
  return dayjs().tz(displayTimezone).add(offsetDays, 'day').format('YYYY-MM-DD')
}

/** `true` cuando quien mira NO está en la zona de la clínica. */
export function viewerIsElsewhere(): boolean {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone !== displayTimezone
  } catch {
    return false
  }
}

function local(value: string) {
  return dayjs(value).tz(displayTimezone)
}

export function formatDateTime(value?: string | null): string {
  return value ? local(value).format('DD/MM/YYYY HH:mm') : '—'
}

export function formatTime(value?: string | null): string {
  return value ? local(value).format('HH:mm') : '—'
}

export function formatDate(value?: string | null): string {
  return value ? local(value).format('DD/MM/YYYY') : '—'
}

/** "hace 2 horas" — en un listado dice más que la fecha exacta. */
// `fromNow` compara dos instantes, así que la zona no le cambia el resultado;
// pasa por ella igualmente para que no haya un formateador con reglas propias.
export function fromNow(value?: string | null): string {
  return value ? local(value).fromNow() : '—'
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('es-MX').format(value)
}

export function formatUsd(value: number): string {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'USD' }).format(value)
}

export function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`
}

/** El número de teléfono en E.164 partido para que se lea de un vistazo. */
export function formatPhone(value?: string | null): string {
  if (!value) return '—'
  const match = value.match(/^\+(\d{1,3})(\d{2,3})(\d{4})(\d{4})$/)
  return match ? `+${match[1]} ${match[2]} ${match[3]} ${match[4]}` : value
}

/** Los días de la promoción viajan en inglés (como los nombra Ruby). */
export const WEEKDAYS: { value: string; label: string }[] = [
  { value: 'sunday', label: 'Dom' },
  { value: 'monday', label: 'Lun' },
  { value: 'tuesday', label: 'Mar' },
  { value: 'wednesday', label: 'Mié' },
  { value: 'thursday', label: 'Jue' },
  { value: 'friday', label: 'Vie' },
  { value: 'saturday', label: 'Sáb' },
]

/**
 * Las intenciones del clasificador, en lenguaje de negocio.
 *
 * En la base son claves técnicas (`other`, `my_appointments`) y así llegaban a
 * la pantalla. Quien la mira no las escribió: lo que necesita saber es qué
 * está pidiendo la gente, no cómo se llama la etiqueta.
 *
 * `other` es la más importante de todas —suele ser la barra más alta— y es la
 * única que no describe una petición sino un fallo: el bot no supo de qué le
 * hablaban. Nombrarla así la convierte en lo que es, un pendiente.
 */
export const INTENT_LABELS: Record<string, string> = {
  other: 'No lo entendió',
  schedule: 'Agendar una cita',
  confirm: 'Confirmar la cita',
  reschedule: 'Cambiar la cita',
  cancel: 'Cancelar la cita',
  my_appointments: 'Consultar sus citas',
  appointment_status: 'Estado de la cita',
  symptom: 'Consulta de síntomas',
  recommendations: 'Indicaciones y recetas',
  lab_results: 'Resultados de laboratorio',
  branch_info: 'Información de la sucursal',
  arrival: 'Aviso de llegada',
  human_agent: 'Pidió hablar con una persona',
  greeting: 'Saludo',
  thanks: 'Agradecimiento',
}

export function intentLabel(intent: string): string {
  return INTENT_LABELS[intent] ?? intent
}

/** Los estados de la máquina del bot, con nombre de negocio. */
export const CONVERSATION_STATES: Record<string, string> = {
  idle: 'En reposo',
  menu: 'En el menú',
  lab_results: 'Resultados de laboratorio',
  scheduling: 'Agendando',
  symptom_help: 'Consulta de síntomas',
  price_spay: 'Cotizando esterilización',
  reminder_reply: 'Respondiendo recordatorio',
  handoff: 'Derivada a una persona',
}

export function conversationStateLabel(state: string): string {
  return CONVERSATION_STATES[state] ?? state
}

/**
 * El canal por el que sale una sucursal, con el nombre de la herramienta en la
 * que el equipo va a buscar el hilo — que es para lo único que se muestra.
 */
export const CHANNEL_LABELS: Record<string, string> = {
  callbell: 'Callbell',
  zendesk: 'Zendesk',
  respira: 'Respira',
}

export function channelLabel(value?: string | null): string {
  if (!value) return '—'
  return CHANNEL_LABELS[value] ?? value
}
